import { PHASES } from '../constants';
import { dispatch, ACTION_TYPES } from '../actions/index';
import { getInstanceDefinition } from '../cards/cardLookup';
import { hasHandler } from '../cards/effectRegistry/index';
import { hasUnmetBanditObligation } from '../cards/effectRegistry/monsters';
import { nextRandom } from '../rng/rng';
import {
  computeHandGoldValue, hasActiveForcedFightObligation, hasAssignedMonsterObligation, getPlayerCollectionInstanceIds,
} from '../state/selectors';
import {
  pickBestBuy, pickBestLevelUp, listAffordableBuySources, DEFAULT_BUY_WEIGHTS,
} from './heuristics/villageHeuristics';
import { synergyScore } from './heuristics/synergyClusters';
import { planEquips, pickBestMonsterToFight, simulateEquippedState } from './heuristics/dungeonHeuristics';
import { evaluatePosition } from './heuristics/positionEvaluation';
import { determinizeAllDecks } from './heuristics/determinize';

// The AI is just another player dispatching through the exact same
// dispatch(state, action) API a human's UI uses - see plan, "AI opponent
// design". Nothing here has special engine-level privileges; playAITurn is
// purely a decision-making loop that calls dispatch() repeatedly, the same
// way a human clicking through the UI would, until the turn ends.

// Caldera Dragon's forced discard and every Raid/Battle/Aftermath forced
// destroy/discard (see abilityActions.js/handChoiceActions.js) interrupt
// normal play with a mandatory follow-up dispatch that dispatch()'s top-
// level guard requires before anything else can happen - a human player
// sees a picker; the bot just needs to not get stuck. Picks the lowest-
// gold-value candidate each time (a reasonable default, not meant to be
// optimal play) and loops since a single trigger can queue more than one
// entry at once (Smokeplume Drake's "discard 2 cards", Charred Bruin's
// "each player destroys 1 card"). Deliberately NOT scoped to the calling
// turn's playerId - some entries (Charred Bruin, Hellhound) are owed to the
// OTHER player in AI-vs-AI self-play, who has no separate turn of their own
// to resolve it on; this bot is simulating every seat, so it resolves
// whichever entry is actually next regardless of whose it is.
function pickLowestGoldValue(state, instanceIds) {
  return [...instanceIds].sort(
    (a, b) => (getInstanceDefinition(state, a).goldValue || 0) - (getInstanceDefinition(state, b).goldValue || 0),
  )[0];
}

// The next owed player for whatever's currently pending (Caldera Dragon's
// discard, or the head of pendingHandChoices) - null if nothing is pending.
// Shared by resolvePendingChoices below and the exported
// resolveAIOwedPendingChoices, which needs to know WHO a pending entry is
// owed to before deciding whether it's safe to auto-resolve.
function nextPendingOwedPlayerId(state) {
  if (state.meta.pendingCalderaDragonDiscard) return state.meta.pendingCalderaDragonDiscard.playerId;
  return state.meta.pendingHandChoices?.[0]?.playerId ?? null;
}

// Resolves exactly the head of whatever's currently pending, with the same
// simple, safe-by-construction heuristics throughout this bot (lowest gold
// value, decline anything optional/risky). Returns state unchanged if
// nothing is pending. Shared by resolvePendingChoices (playAITurn's own
// unconditional loop) and resolveAIOwedPendingChoices (called for entries
// owed to an AI player regardless of whose turn it currently is - see that
// function's own comment).
function resolveOnePendingChoice(state) {
  const s = state;
  if (s.meta.pendingCalderaDragonDiscard) {
    const { playerId } = s.meta.pendingCalderaDragonDiscard;
    const player = s.players.find((p) => p.id === playerId);
    const target = pickLowestGoldValue(s, player.hand);
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_CALDERA_DRAGON_DISCARD, payload: { playerId, instanceId: target } });
  }
  if (s.meta.pendingHandChoices?.[0]?.kind === 'optionalDiscard') {
    // Blazing Grimalkin: "discard 3 cards, or lose 1 XP" - the bot always
    // declines (same practical default as before this queue existed: the
    // old choices?.discardingPlayerIds fallback was dead code, so nothing
    // ever opted in). A 1-XP cost is simpler and safer than gambling 3
    // unknown-value cards for a bot with no hand-value judgment.
    const { playerId } = s.meta.pendingHandChoices[0];
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_OPTIONAL_DISCARD, payload: { playerId, accept: false } });
  }
  if (s.meta.pendingHandChoices?.[0]?.kind === 'optionalCurseSelfDestroy') {
    // Curse of War: "select 2 random cards... you may destroy 1 of them to
    // destroy this card." Same conservative bias as optionalDiscard above -
    // the bot always declines rather than gambling away one of 2 unknown-
    // value random cards it has no way to judge against keeping the curse.
    const { playerId } = s.meta.pendingHandChoices[0];
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_OPTIONAL_CURSE_DESTROY, payload: { playerId, accept: false } });
  }
  if (s.meta.pendingHandChoices?.[0]?.kind === 'effectChoice') {
    // Purple Worm: "discard 2 cards or destroy 1 hero" - picks whichever
    // option ISN'T 'destroy' (same conservative "don't lose a hero to an
    // ability it doesn't understand" bias as tryRemoveCursesAndUseShards'
    // own comment), falling back to the first option if 'destroy' is
    // somehow the only one offered.
    const { playerId, options } = s.meta.pendingHandChoices[0];
    const key = (options.find((o) => o.key !== 'destroy') || options[0]).key;
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_EFFECT_CHOICE, payload: { playerId, key } });
  }
  if (s.meta.pendingHandChoices?.[0]?.kind === 'shieldBearerOffer') {
    // Same conservative bias as optionalDiscard above - the bot always
    // declines rather than gambling away a Shield Bearer it has no way to
    // value against the hero it would be protecting.
    const { playerId } = s.meta.pendingHandChoices[0];
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_SHIELD_BEARER_OFFER, payload: { playerId, accept: false } });
  }
  if (s.meta.pendingHandChoices?.[0]?.kind === 'intelligentReactOffer') {
    // Unlike Shield Bearer/optionalDiscard above, this one has no
    // downside - same "always worth using" bias as
    // tryRemoveCursesAndUseShards' own curse/shard defaults.
    const { playerId } = s.meta.pendingHandChoices[0];
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_INTELLIGENT_REACT_OFFER, payload: { playerId, accept: true } });
  }
  if (s.meta.pendingHandChoices?.[0]?.kind === 'preparePick') {
    // Enhanced Rugged Jack's bonus prepare - same keep-heuristic as the
    // bot's own top-level Prepare turn action (pickPrepareKeep). No
    // `weights` in scope this deep in the pending-choice chain, so this
    // always uses pickPrepareKeep's DEFAULT_BUY_WEIGHTS default
    // (synergyWeight: 0) rather than the acting player's actual difficulty
    // - a small, explicitly-scoped gap for a rarely-triggered bonus action,
    // not an oversight (see pickPrepareKeep's own comment).
    const { playerId } = s.meta.pendingHandChoices[0];
    const orderedInstanceIds = pickPrepareKeep(s, playerId);
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_PREPARE_PICK, payload: { playerId, orderedInstanceIds } });
  }
  if (s.meta.pendingHandChoices?.length) {
    const { playerId, candidateInstanceIds, kind } = s.meta.pendingHandChoices[0];
    // candidateInstanceIds can be stale for a player's own 2nd+ queued
    // entry (e.g. Smokeplume Drake's "discard 2 cards" queues 2 entries
    // off the SAME pre-effect hand) - filter to what's actually still in
    // hand before picking, or this could try to resolve with a card the
    // first entry already removed. Gnoll's own shape (kind:
    // 'discardPileChoice' - effectHelpers.js's queueDiscardPileChoice)
    // is the one exception: its candidates were revealed FROM discard,
    // never touched hand at all, same zone distinction
    // handChoiceActions.js's dropDeadEntries already makes.
    const player = s.players.find((p) => p.id === playerId);
    const zone = kind === 'discardPileChoice' ? player.discard : player.hand;
    const target = pickLowestGoldValue(s, candidateInstanceIds.filter((id) => zone.includes(id)));
    return dispatch(s, { type: ACTION_TYPES.RESOLVE_HAND_CHOICE, payload: { playerId, instanceId: target } });
  }
  return s;
}

function resolvePendingChoices(state) {
  // A monster's own Aftermath can end the game outright mid-combat-resolution
  // (e.g. Heart of Doom's "if not defeated, the active player loses" -
  // effectHelpers.js's eliminatePlayer - leaving only 1 player standing)
  // while ANOTHER hand card's Aftermath, resolved in that same combat pass,
  // still queued its own pending choice - now permanently moot, since the
  // game already ended on that exact fight. dispatch() itself refuses every
  // action once meta.gameOver is true (by design - nothing should mutate
  // state after the game ends), so trying to drain that leftover entry here
  // would throw "The game is already over." even though this is just
  // cleanup, not a new move. GameBoard.vue already stops showing any
  // player-facing UI the instant gameOver flips, so there's no one left to
  // answer it anyway - just stop, same as the UI does.
  if (state.meta.gameOver) return state;
  let s = state;
  for (let guard = 0; guard < 20; guard += 1) {
    const before = s;
    s = resolveOnePendingChoice(s);
    if (s === before) break;
  }
  return s;
}

// Matt reported this directly: "The Hellhound has a battle effect that says
// each other player must discard a hero... When playing against the
// computer, it asks the player to decide which hero the computer discards.
// But the computer should choose for itself." Root cause: pending choices
// owed to a non-active player (Hellhound's "each OTHER player", Charred
// Bruin's "each player destroys 1 card", etc.) only ever got auto-resolved
// as part of resolvePendingChoices above, which is itself only reachable
// from inside playAITurn - i.e. only when it's that player's OWN turn.
// When a human's action queues a choice owed to an AI opponent mid the
// human's turn, nothing was resolving it, so PlayerDock.vue's
// viewingPlayer fell back to its hotseat-sharing behavior (temporarily
// showing the owed player's hand on the one shared screen) - correct for a
// second HUMAN player in local hotseat, wrong for an AI, who has no
// "screen" to make a real choice on at all.
//
// Called from store/index.js's submitAction right after every dispatch
// (human or AI), before anything gets rendered - loops for as long as the
// pending entry is owed to an isAI player, using the exact same
// resolveOnePendingChoice heuristics playAITurn already trusts, and stops
// the moment a pending entry (if any) is owed to a human - that one still
// needs to surface normally (a second human in local hotseat, or the
// player's own turn in a remote game).
export function resolveAIOwedPendingChoices(state) {
  // Same reasoning as resolvePendingChoices above - a leftover pending
  // choice can be left queued (and now moot) by the exact fight that just
  // ended the game (e.g. Heart of Doom eliminating the last opponent while
  // another hand card's own Aftermath queued something in that same
  // resolution). This call site has the identical exposure: store/index.js
  // calls it after EVERY dispatch, human or AI, so a human's own
  // game-ending move could hit it too.
  if (state.meta.gameOver) return state;
  let s = state;
  for (let guard = 0; guard < 20; guard += 1) {
    const owedId = nextPendingOwedPlayerId(s);
    if (!owedId) break;
    const owedPlayer = s.players.find((p) => p.id === owedId);
    if (!owedPlayer?.isAI) break;
    s = resolveOnePendingChoice(s);
  }
  return s;
}

// Best-effort, safe-by-construction ability usage: curses are (almost)
// always worth removing, and Thunderstone Shard's Strength bonus has no
// downside, so the bot uses these opportunistically without needing a full
// cost/benefit model. Every other card's Village/Dungeon ability is left
// unused by the bot for now (a human or a future, more specific heuristic can
// use them) - deliberately conservative rather than risking the bot
// destroying its own good cards via an ability it doesn't understand yet.
function tryRemoveCursesAndUseShards(state, playerId, timing) {
  let s = state;
  for (let guard = 0; guard < 20; guard += 1) {
    const player = s.players.find((p) => p.id === playerId);
    const target = player.hand.find((instanceId) => {
      const def = getInstanceDefinition(s, instanceId);
      if (def.type !== 'curse' && def.id !== 'basic-thunderstone-shard') return false;
      const abilities = def.abilityText[timing] || [];
      return abilities.some((entry, abilityIndex) => {
        if (!hasHandler(def.id, timing, abilityIndex)) return false;
        const key = `${instanceId}:${timing}:${abilityIndex}`;
        return entry.repeatable || !player.turnFlags.usedAbilityInstanceIds.includes(key);
      });
    });
    if (!target) break;
    const def = getInstanceDefinition(s, target);
    const abilityIndex = (def.abilityText[timing] || []).findIndex((entry, i) => hasHandler(def.id, timing, i));
    try {
      s = dispatch(s, {
        type: timing === 'village' ? ACTION_TYPES.USE_VILLAGE_ABILITY : ACTION_TYPES.USE_DUNGEON_ABILITY,
        payload: { playerId, instanceId: target, abilityIndex },
      });
      s = resolvePendingChoices(s); // e.g. this ability drew a card and Caldera Dragon is present
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[ai-fallback-used] ${def.id}:${timing}:${abilityIndex} failed, skipping: ${err.message}`);
      break; // avoid retrying the same failing ability forever
    }
  }
  return s;
}

// Matt reported this directly: "The computer defeated at least one enemy but
// they didn't take a familiar." Root cause: taking a familiar (rulebook,
// "Familiars" - a once-per-game option offered as a Spoils ability right
// after ANY win, not tied to a specific card or a pendingChoices entry) is
// its own top-level action, TAKE_FAMILIAR, gated only on
// meta.spoilsWindowOpen - nothing in playDungeonTurn ever dispatched it, so
// the bot silently let the window close unused every single time. Same
// "no real downside" reasoning as tryRemoveCursesAndUseShards above: a free
// card, usable at most once per game, is always worth taking if the deck
// isn't empty and this player hasn't already got one - so no cost/benefit
// model is needed, just remembering to ask for it.
function tryTakeFamiliar(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  if (player.familiarInstanceId) return state;
  if (!state.village.familiarDeck.length) return state;
  try {
    return dispatch(state, { type: ACTION_TYPES.TAKE_FAMILIAR, payload: { playerId } });
  } catch (err) {
    // Defensive only - takeFamiliar's own guards (spoils window, already
    // taken, empty deck) are all checked above already.
    // eslint-disable-next-line no-console
    console.warn(`[ai-fallback-used] TAKE_FAMILIAR failed, skipping: ${err.message}`);
    return state;
  }
}

// Numenera, Bandit Taskmaster/Bandit Warrior: "Global: You must use all
// Dungeon abilities (if possible)." Now a hard rule (actions/index.js's
// CHOOSE_MONSTER/END_DUNGEON_TURN throw if unmet) - the bot's normal
// Dungeon-ability usage is deliberately conservative
// (tryRemoveCursesAndUseShards only, curses/shards), so this widens to
// every currently-usable ability, but ONLY while the obligation actually
// applies (hasUnmetBanditObligation already gates on hall presence) -
// otherwise the bot stays exactly as conservative as before. Dry-runs each
// candidate the same way hasUnmetBanditObligation itself does (see that
// function's own comment - actually dispatching and discarding the result,
// rather than a separate isUsable/getCandidates-based prediction that could
// drift out of sync with what dispatch actually accepts).
function findNextUsableDungeonAbility(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  for (const instanceId of player.hand) {
    const def = getInstanceDefinition(state, instanceId);
    const entries = def.abilityText.dungeon || [];
    for (let abilityIndex = 0; abilityIndex < entries.length; abilityIndex += 1) {
      const key = `${instanceId}:dungeon:${abilityIndex}`;
      if (player.turnFlags.triggeredAbilityInstanceIds.includes(key)) continue;
      try {
        dispatch(state, { type: ACTION_TYPES.USE_DUNGEON_ABILITY, payload: { playerId, instanceId, abilityIndex, choices: {} } });
      } catch {
        continue;
      }
      return { instanceId, abilityIndex };
    }
  }
  return null;
}

function satisfyBanditObligation(state, playerId) {
  let s = state;
  for (let guard = 0; guard < 20 && hasUnmetBanditObligation(s, playerId); guard += 1) {
    const picked = findNextUsableDungeonAbility(s, playerId);
    if (!picked) break; // shouldn't happen (hasUnmetBanditObligation just said yes) - avoid an infinite loop regardless
    try {
      s = dispatch(s, { type: ACTION_TYPES.USE_DUNGEON_ABILITY, payload: { playerId, ...picked } });
      s = resolvePendingChoices(s); // e.g. this ability drew a card and Caldera Dragon is present
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[ai-fallback-used] bandit-obligation ${picked.instanceId}:dungeon:${picked.abilityIndex} failed, skipping: ${err.message}`);
      break; // avoid retrying the same failing ability forever
    }
  }
  return s;
}

// Applies weights.mistakeChance on top of pickBestBuy's real best pick -
// villageHeuristics.js's difficulty-tier lever. Consumes state.meta.rngState
// via engine/rng/rng.js (never Math.random() - eslint-enforced for
// src/engine/**), same threading pattern as effectHelpers.js's
// pickRandomInstanceId, so it returns { state, buySource } rather than a
// bare value. Only ever called from playVillageTurn's actual BUY_CARD
// dispatch site below - NOT from decideTopLevelAction's own pickBestBuy
// call, which is a pure scoring probe (dungeon vs. village vs. rest vs.
// prepare) that must stay deterministic, or games could start behaving
// unpredictably in ways the self-play turn-cap tests were never tuned to
// tolerate (see this file's and dungeonHeuristics.js's own comments on
// bugs previously caught only by self-play games failing to terminate).
function pickBestBuyWithMistake(state, playerId, weights) {
  const best = pickBestBuy(state, playerId, weights);
  const mistakeChance = weights.mistakeChance || 0;
  if (mistakeChance <= 0) return { state, buySource: best };
  const [roll, rngState] = nextRandom(state.meta.rngState);
  let s = { ...state, meta: { ...state.meta, rngState } };
  if (roll >= mistakeChance) return { state: s, buySource: best };
  // Mistake triggered: either skip the buy entirely, or take a random
  // affordable alternative instead of the top-scored one - split the
  // remaining roll rather than always producing the same kind of "dumb."
  const [subRoll, rngState2] = nextRandom(s.meta.rngState);
  s = { ...s, meta: { ...s.meta, rngState: rngState2 } };
  if (subRoll < 0.5 || !best) return { state: s, buySource: null };
  const affordable = listAffordableBuySources(s, playerId);
  const [pickRoll, rngState3] = nextRandom(s.meta.rngState);
  s = { ...s, meta: { ...s.meta, rngState: rngState3 } };
  const alt = affordable[Math.floor(pickRoll * affordable.length)] || best;
  return { state: s, buySource: alt };
}

function playVillageTurn(state, playerId, weights) {
  let s = dispatch(state, { type: ACTION_TYPES.VISIT_VILLAGE, payload: { playerId } });
  // Root of Corruption, Neglect Incarnate: "Trophy: Discard down to 5
  // cards..." - the first Trophy effect that can queue a pending hand
  // choice purely from visiting the village (no combat needed). Trophy
  // effects resolve as part of the VISIT_VILLAGE dispatch itself, so this
  // must run before anything else touches the hand.
  s = resolvePendingChoices(s);
  s = tryRemoveCursesAndUseShards(s, playerId, 'village');

  const levelUpTarget = pickBestLevelUp(s, playerId);
  if (levelUpTarget) {
    try {
      s = dispatch(s, { type: ACTION_TYPES.LEVEL_UP_HERO, payload: { playerId, heroInstanceId: levelUpTarget } });
      // Leveling up destroys the old hero card - can queue an Intelligent
      // React offer (effectHelpers.js's offerIntelligentReact) that must be
      // resolved before any other action is legal.
      s = resolvePendingChoices(s);
    } catch { /* heuristic was wrong about legality somehow - just skip */ }
  }

  const { state: afterMistakeRoll, buySource } = pickBestBuyWithMistake(s, playerId, weights);
  s = afterMistakeRoll;
  if (buySource) {
    try {
      s = dispatch(s, { type: ACTION_TYPES.BUY_CARD, payload: { playerId, source: buySource } });
    } catch { /* someone/something changed gold mid-decision - skip */ }
  }

  return dispatch(s, { type: ACTION_TYPES.END_VILLAGE_TURN, payload: { playerId } });
}

function playDungeonTurn(state, playerId) {
  let s = dispatch(state, { type: ACTION_TYPES.ENTER_DUNGEON, payload: { playerId } });
  // Same Trophy-on-reveal reason as playVillageTurn above - ENTER_DUNGEON
  // also runs resolveTrophyEffects before anything else here touches the
  // hand.
  s = resolvePendingChoices(s);
  s = tryRemoveCursesAndUseShards(s, playerId, 'dungeon');

  for (const { heroInstanceId, weaponInstanceId } of planEquips(s, playerId)) {
    try {
      s = dispatch(s, { type: ACTION_TYPES.EQUIP_WEAPON, payload: { playerId, heroInstanceId, weaponInstanceId } });
    } catch { /* plan can go stale if an ability changed hand contents - skip that pairing */ }
  }

  // Equipping can unlock a previously-unusable Dungeon ability (e.g. a
  // polearm-conditional one), so this runs after planEquips above, right
  // before the fight/end-turn decision that would otherwise be blocked by
  // an unmet obligation.
  s = satisfyBanditObligation(s, playerId);

  const best = pickBestMonsterToFight(s, playerId);
  if (best) {
    s = dispatch(s, { type: ACTION_TYPES.CHOOSE_MONSTER, payload: { playerId, hallRankIndex: best.hallRankIndex } });
    s = resolvePendingChoices(s); // Battle/Aftermath/Raid may have just queued a forced destroy/discard choice
    if (process.env.AI_DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[dungeon] ${playerId} predicted won=${best.won} actual won=${s.meta.lastCombatResult.won} monster=${best.monsterDef.title} health=${best.monsterDef.health} attack=${s.meta.lastCombatResult.totalAttack}`);
    }
    if (s.meta.gameOver) return s;
    if (s.meta.spoilsWindowOpen) {
      s = tryRemoveCursesAndUseShards(s, playerId, 'spoils'); // shards/curses have no spoils abilities, but harmless to check
      s = tryTakeFamiliar(s, playerId);
    }
  }

  return dispatch(s, { type: ACTION_TYPES.END_DUNGEON_TURN, payload: { playerId } });
}

// How much a hand card is worth keeping around - goldValue + strength as a
// cheap raw-stat proxy (real cardValue lives in villageHeuristics.js and
// isn't exported; this mirrors it loosely, same level of "good enough, not
// exact" this whole file's heuristics already accept) plus synergyScore so
// combo pieces the player is already building toward outweigh an isolated
// card with equal raw stats. Curses always score lowest - never worth
// keeping, always worth destroying. Shared by pickPrepareKeep (keeps the
// highest-scoring cards) and playRestTurn (destroys the LOWEST-scoring
// one) - two mirror-image uses of the same "how good is this card" call,
// not two different definitions of it. A prior version of playRestTurn's
// destroy-target scored by synergyScore ALONE: with synergyWeight off (the
// exact condition under which "pure deck-thinning" was tested), that
// collapsed to a same-value-for-every-card comparator, i.e. no actual
// sorting at all - Rest was destroying essentially whatever was first in
// hand, not a deliberate worst-card choice. That bug, not deck-thinning
// itself, is why restBloatWeight looked like a clean loser in
// scripts/rosters/thinning.json - see DEFAULT_BUY_WEIGHTS.restBloatWeight's
// own comment for the corrected re-sweep.
function handCardKeepScore(state, playerId, def, weights, synergyMultiplier, activeClusterIds) {
  return def.type === 'curse' ? -10
    : (def.goldValue || 0) + (def.strength || 0) + synergyScore(state, playerId, def, weights.clusterCap, activeClusterIds) * synergyMultiplier;
}

// Prepare: keep the most valuable-looking cards on top of the deck (by gold
// value + strength + synergy fit), discard the rest. Simple and rarely the
// best of the four actions, but a reasonable fallback when neither Village
// nor Dungeon scores well (see decideTopLevelAction). Matt: "you wanna draw
// them consistently" - the synergyScore term keeps combo pieces the player
// is already building toward on top of the deck, not just raw stats.
// Shared by the top-level Prepare turn action and Enhanced Rugged Jack's
// React-granted bonus prepare below (which calls this with no `weights` -
// that rare mid-dungeon-turn bonus is reached through many pending-choice
// call sites that don't thread weights, so it always uses
// DEFAULT_BUY_WEIGHTS/synergyWeight: 0 rather than a much larger plumbing
// change for a rarely-triggered action) - same "keep the best 3, never a
// curse" heuristic either way.
// Exported (alongside playRestTurn below) purely for direct unit testing,
// same reasoning villageHeuristics.js already exports its pure scoring
// functions - not part of the AI's own public entry point (playAITurn is).
export function pickPrepareKeep(state, playerId, weights = DEFAULT_BUY_WEIGHTS) {
  const player = state.players.find((p) => p.id === playerId);
  // Gated on state.meta.randomizedVillage, same as villageHeuristics.js's
  // costEfficiency - see that function's own comment for the empirical
  // (A/B sweep) reason synergy is a real regression on the standard
  // curated board, not just a smaller effect.
  const synergyMultiplier = state.meta?.randomizedVillage ? weights.synergyWeight : 0;
  const activeClusterIds = weights.activeClusters ? new Set(weights.activeClusters) : null;
  const ordered = [...player.hand].sort((a, b) => {
    const defA = getInstanceDefinition(state, a);
    const defB = getInstanceDefinition(state, b);
    return handCardKeepScore(state, playerId, defB, weights, synergyMultiplier, activeClusterIds)
      - handCardKeepScore(state, playerId, defA, weights, synergyMultiplier, activeClusterIds);
  });
  return ordered.filter((id) => getInstanceDefinition(state, id).type !== 'curse').slice(0, 3);
}

function playPrepareTurn(state, playerId, weights) {
  const keep = pickPrepareKeep(state, playerId, weights);
  return dispatch(state, { type: ACTION_TYPES.PREPARE, payload: { playerId, orderedInstanceIds: keep } });
}

// Rest: destroy a curse if present (unchanged), else the lowest-value card
// in hand (handCardKeepScore - real deck-thinning chaff, not the
// previously-arbitrary hand[0], and not the earlier synergy-only sort that
// silently degenerated into hand[0] whenever synergyWeight was off - see
// handCardKeepScore's own comment).
// Exported for direct unit testing - see pickPrepareKeep's own comment.
export function playRestTurn(state, playerId, weights = DEFAULT_BUY_WEIGHTS) {
  const player = state.players.find((p) => p.id === playerId);
  const curseId = player.hand.find((id) => getInstanceDefinition(state, id).type === 'curse');
  // Gated on state.meta.randomizedVillage - see pickPrepareKeep's own comment.
  const synergyMultiplier = state.meta?.randomizedVillage ? weights.synergyWeight : 0;
  const activeClusterIds = weights.activeClusters ? new Set(weights.activeClusters) : null;
  let target = curseId || [...player.hand].sort(
    (a, b) => handCardKeepScore(state, playerId, getInstanceDefinition(state, a), weights, synergyMultiplier, activeClusterIds)
      - handCardKeepScore(state, playerId, getInstanceDefinition(state, b), weights, synergyMultiplier, activeClusterIds),
  )[0];
  // Defensive: restActions.js's rest() requires instanceIdToDestroy to
  // actually be in hand (Rest always destroys exactly one card, no "empty
  // rest"). decideTopLevelAction's computeRestScore already refuses to
  // choose 'rest' with an empty hand, so this should be unreachable - an
  // intermittent self-play crash surfaced this exact "not in hand" dispatch
  // failure under a heavy restBloatWeight without a clean, reproducible
  // root cause found, so this validates the computed target directly
  // rather than trusting it, same "AI's own pick turned out illegal, log
  // and fall back" pattern tryRemoveCursesAndUseShards/
  // satisfyBanditObligation already use elsewhere in this file.
  if (!target || !player.hand.includes(target)) {
    // eslint-disable-next-line no-console
    console.warn(`[ai-fallback-used] playRestTurn: computed target ${target} not in hand, falling back to hand[0]`);
    target = player.hand[0];
  }
  const s = dispatch(state, { type: ACTION_TYPES.REST, payload: { playerId, instanceIdToDestroy: target } });
  // Rest destroys a card too - same Intelligent React risk as LEVEL_UP_HERO
  // above (playVillageTurn).
  return resolvePendingChoices(s);
}

// Matt: "destroying cards as a strategy" / "consider total cards in the
// deck" - a bloated deck draws its combo pieces less reliably, and Rest is
// the AI's only thinning tool. 0 when weights.restBloatWeight is 0 (the
// default) - an exact no-op, preserving decideTopLevelAction's old
// behavior where Rest was never a scored competitor to dungeon/village at
// all (only ever reachable via the hasCurse shortcut above it). Untested
// as of writing - see DEFAULT_BUY_WEIGHTS.restBloatWeight's own comment;
// scripts/aiRoundRobin.mjs's roster is where this gets real scrutiny.
function computeRestScore(state, playerId, weights) {
  if (!weights.restBloatWeight) return 0;
  // Rest always destroys exactly one hand card (restActions.js's rest() -
  // there's no "empty rest," the rulebook action always thins something) -
  // with no legal target, Rest can never be chosen, however bloated the
  // deck is. A real self-play crash caught this: a high enough
  // restBloatWeight can genuinely destroy a player's entire collection
  // down to 0 cards over a long enough game (every card, in every zone,
  // eventually Rest-destroyed one at a time) before this guard existed.
  const player = state.players.find((p) => p.id === playerId);
  if (player.hand.length === 0) return 0;
  return getPlayerCollectionInstanceIds(state, playerId).length * weights.restBloatWeight;
}

// Matt, after the destroy-target fix still didn't save restBloatWeight
// (see its own comment - the re-swept result got WORSE, not better,
// confirming the fix was correct but deck-thinning's real problem was
// never what got destroyed): "you don't just delete cards in order to
// win, you delete the cards on the turns where you don't have anything
// else to do... If a turn can go to the dungeon, it should go to the
// dungeon. If it can't defeat a monster... go to the village and buy the
// best card. And if [that] isn't worth it, trash a card. And if none of
// those are valuable enough, prepare." This is a strict PRIORITY ORDER,
// not a magnitude comparison like decideTopLevelAction's default scoring
// - Rest should never outbid a perfectly good Dungeon/Village turn just
// because a bloat-count score happened to be numerically higher, which is
// exactly what the old restScore-vs-dungeonScore-vs-villageScore
// comparison allowed. Gated behind weights.priorityOrderedActions (new,
// default false - decideTopLevelAction's existing scored comparison is
// untouched when this is off) so it gets the same tournament scrutiny
// every other new idea this session got before being trusted.
function decideTopLevelActionByPriority(state, playerId, weights, best, buySource, levelUpTarget) {
  const player = state.players.find((p) => p.id === playerId);
  if (best && best.won) return 'dungeon';
  const charmBlocksVillage = player.hand.some((id) => getInstanceDefinition(state, id).id === 'village-charm-of-venery');
  if ((buySource || levelUpTarget) && !charmBlocksVillage) return 'village';
  // "Trash a card" only when there's a genuinely bad one sitting in hand
  // right now (handCardKeepScore below restJunkThreshold) - a direct
  // "is my hand carrying junk" read, not the old restBloatWeight's
  // indirect "my whole collection is large" proxy, which the destroy-
  // target fix already showed wasn't the real problem with deck-thinning.
  if (player.hand.length > 0) {
    const synergyMultiplier = state.meta?.randomizedVillage ? weights.synergyWeight : 0;
    const activeClusterIds = weights.activeClusters ? new Set(weights.activeClusters) : null;
    const worstScore = Math.min(...player.hand.map(
      (id) => handCardKeepScore(state, playerId, getInstanceDefinition(state, id), weights, synergyMultiplier, activeClusterIds),
    ));
    if (worstScore < weights.restJunkThreshold) return 'rest';
  }
  return 'prepare';
}

// Scores the top-level actions and returns the highest. Dungeon progress is
// what ultimately ends the game, so it's weighted to win most ties; Rest is
// strongly preferred whenever a curse is sitting in hand, and otherwise
// only competes via restBloatWeight (off by default - see its own comment).
// Exported for direct unit testing - see pickPrepareKeep's own comment.
export function decideTopLevelAction(state, playerId, weights) {
  const player = state.players.find((p) => p.id === playerId);
  // Caverns of Bane, Troll Skullcracker's Raid obligation - actions/index.js
  // blocks Visit/Prepare/Rest outright for the obligated player, so (like
  // Charm of Venery below) the AI must not even attempt them. Checked before
  // the curse/hasCurse 'rest' shortcut, since Rest is one of the blocked
  // actions too.
  if (hasActiveForcedFightObligation(state, playerId)) return 'dungeon';
  // Root of Corruption, Mind Control - actions/index.js blocks Visit/
  // Prepare/Rest outright for the obligated player, same shape as the Troll
  // Skullcracker check just above.
  if (hasAssignedMonsterObligation(state, playerId)) return 'dungeon';
  // Caverns of Bane, Curse of War: "You cannot rest or prepare." - checked
  // before the general hasCurse/'rest' shortcut just below, since War is
  // itself a curse and would otherwise trigger the exact action it forbids.
  const blockedFromRestOrPrepare = player.hand.some((id) => getInstanceDefinition(state, id).id === 'curse-war');
  const hasCurse = player.hand.some((id) => getInstanceDefinition(state, id).type === 'curse');
  if (hasCurse && !blockedFromRestOrPrepare) return 'rest';

  // Score against the hand as it WOULD be if the bot entered the dungeon and
  // equipped everything it could - not the current unequipped hand - or
  // every fight looks weaker than it really is and the bot avoids the
  // dungeon far more than it should (a real bug caught via self-play games
  // that never terminated within a generous turn cap).
  const best = pickBestMonsterToFight(simulateEquippedState(state, playerId), playerId);
  const buySource = pickBestBuy(state, playerId, weights);
  const levelUpTarget = pickBestLevelUp(state, playerId);

  if (weights.priorityOrderedActions) {
    let choice = decideTopLevelActionByPriority(state, playerId, weights, best, buySource, levelUpTarget);
    // Curse of War blocks both 'rest' and 'prepare' - unlike Charm of
    // Venery's single blocked action, there's no in-between fallback to
    // reach for. Prefers 'dungeon' when there's actually something worth
    // fighting (best truthy); otherwise 'village', since ending a village
    // turn never requires having bought anything (unlike ending a dungeon
    // turn, which throws if something was fightable and nothing was fought
    // - see actions/index.js's END_DUNGEON_TURN case).
    if (blockedFromRestOrPrepare && (choice === 'rest' || choice === 'prepare')) {
      choice = best ? 'dungeon' : 'village';
    }
    if (process.env.AI_DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[decide-priority] ${playerId} -> ${choice}`);
    }
    return choice;
  }

  const dungeonScore = best ? (best.won ? best.value + 1 : 0.3) : 0;
  const gold = computeHandGoldValue(state, playerId);
  const villageScore = (buySource ? 2 : 0) + (levelUpTarget ? 2 : 0) + gold * 0.1;
  const restScore = computeRestScore(state, playerId, weights);

  // With restScore === 0 (weights.restBloatWeight off, the default), this
  // reduces to exactly the old two-way ternary: restScore can never win
  // (villageScore/dungeonScore are always >= 0), so the `else` branch below
  // is unreachable and behavior is byte-identical to before this restructure.
  let choice;
  if (dungeonScore >= villageScore && dungeonScore >= restScore) {
    choice = dungeonScore > 0 ? 'dungeon' : 'prepare';
  } else if (villageScore >= restScore) {
    choice = 'village';
  } else {
    choice = 'rest';
  }
  // Caverns of Bane, Charm of Venery: "When this card is present, you may
  // not visit the village." - actions/index.js enforces this at dispatch
  // time; the AI must not even attempt it (falls back to whichever of
  // dungeon/prepare it would have picked as the runner-up).
  if (choice === 'village' && player.hand.some((id) => getInstanceDefinition(state, id).id === 'village-charm-of-venery')) {
    choice = dungeonScore > 0 ? 'dungeon' : 'prepare';
  }
  // Caverns of Bane, Curse of War: "You cannot rest or prepare." Same
  // redirect shape as Charm of Venery just above, but for 'rest'/'prepare'
  // instead of 'village' - falls back to 'village' rather than 'prepare'
  // when there's nothing worth fighting, since 'prepare' is itself blocked.
  if (blockedFromRestOrPrepare && (choice === 'rest' || choice === 'prepare')) {
    choice = dungeonScore > 0 ? 'dungeon' : 'village';
  }
  if (process.env.AI_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`[decide] ${playerId} dungeonScore=${dungeonScore.toFixed(2)} villageScore=${villageScore.toFixed(2)} restScore=${restScore.toFixed(2)} -> ${choice}`);
  }
  return choice;
}

// Real lookahead, not another single-step scoring heuristic. Matt: "let's
// go ahead and build an actual look ahead system... aim big... make this
// really good." Every decision so far this session (buy scoring, synergy,
// deck-thinning, priority order) only ever evaluated the CURRENT turn in
// isolation - none of it could reason about what happens two or three
// turns from now, which is exactly the gap flagged as the likely ceiling
// on how much further tuning could improve this bot.
//
// Design: at the top-level decision point, actually PLAY each legal turn
// type for real (dungeon/village/rest/prepare - reusing the existing
// playDungeonTurn/playVillageTurn/playRestTurn/playPrepareTurn functions
// unchanged), then roll the game forward `weights.lookaheadDepth` more
// simulated turns per candidate, `weights.lookaheadSamples` times each,
// and score the resulting positions with positionEvaluation.js's
// evaluatePosition. Whichever real candidate leads to the best AVERAGE
// simulated outcome is the one actually taken - the AI is judged by where
// its choices actually lead, not a same-turn point estimate.
//
// The one subtlety that matters: the engine's state is fully transparent
// (every deck's exact order is a real, visible array), so a naive rollout
// could let the AI "see" its own literal future draws while planning -
// unlike anything a real player (human or otherwise) actually knows about
// their own shuffled deck. determinize.js's determinizeAllDecks reshuffles
// every player's deck via the seeded RNG before each simulated sample, so
// the search reasons honestly about "what MIGHT happen," sampled several
// times and averaged (a simplified form of Perfect Information Monte
// Carlo, a standard, well-regarded technique for exactly this kind of
// hidden-information search), not "what WILL happen because I peeked."
//
// Rollout continuations always use lookaheadDepth: 0 (the plain, fast
// heuristic) rather than recursing into another full search - the ROOT
// decision gets the deep evaluation, continuations are a quick, symmetric
// stand-in for "how would a reasonable player continue from here," the
// same simplification real Monte Carlo rollout search uses (search hard
// at the root, play out cheaply and fast beyond it). Without this, cost
// would explode multiplicatively with depth instead of growing linearly.
function rolloutValue(candidateState, playerId, weights, depth, samples) {
  if (candidateState.meta.gameOver || depth <= 0 || samples <= 0) {
    return evaluatePosition(candidateState, playerId);
  }
  const rolloutWeights = { ...weights, lookaheadDepth: 0 };
  let total = 0;
  for (let i = 0; i < samples; i += 1) {
    let s = determinizeAllDecks(candidateState);
    for (let turn = 0; turn < depth && !s.meta.gameOver; turn += 1) {
      const active = s.players[s.meta.activePlayerIndex];
      s = playAITurn(s, active.id, rolloutWeights);
    }
    total += evaluatePosition(s, playerId);
  }
  return total / samples;
}

// Tries each legal top-level turn type for real (skipping any that throw -
// e.g. Caverns of Bane, Charm of Venery blocking Village at dispatch time -
// same defensive "AI's own candidate turned out illegal, treat it as
// unavailable rather than crash" pattern already used throughout this
// file), scores each via rolloutValue, returns the best-scoring outcome
// (already fully computed - no need to redo the winning turn).
function playAITurnWithLookahead(state, playerId, weights) {
  const player = state.players.find((p) => p.id === playerId);
  // Same hard obligations decideTopLevelAction enforces - these aren't
  // preferences the search should weigh, dispatch() itself throws if
  // Village/Rest/Prepare are attempted while one of these applies.
  if (hasActiveForcedFightObligation(state, playerId)) return playDungeonTurn(state, playerId);
  if (hasAssignedMonsterObligation(state, playerId)) return playDungeonTurn(state, playerId);
  // Caverns of Bane, Curse of War: "You cannot rest or prepare." - checked
  // before the general hasCurse shortcut just below, since War is itself a
  // curse and would otherwise trigger the exact action it forbids (same
  // fix as decideTopLevelAction's own blockedFromRestOrPrepare above).
  const blockedFromRestOrPrepare = player.hand.some((id) => getInstanceDefinition(state, id).id === 'curse-war');
  const hasCurse = player.hand.some((id) => getInstanceDefinition(state, id).type === 'curse');
  if (hasCurse && !blockedFromRestOrPrepare) return playRestTurn(state, playerId, weights);

  const candidateResults = [];
  for (const playTurn of [
    () => playDungeonTurn(state, playerId),
    () => playVillageTurn(state, playerId, weights),
    () => playRestTurn(state, playerId, weights),
    () => playPrepareTurn(state, playerId, weights),
  ]) {
    try {
      candidateResults.push(playTurn());
    } catch { /* illegal this turn (e.g. Charm of Venery blocking Village) - not a candidate */ }
  }

  let best = null;
  let bestValue = -Infinity;
  for (const result of candidateResults) {
    const value = rolloutValue(result, playerId, weights, weights.lookaheadDepth, weights.lookaheadSamples);
    if (value > bestValue) {
      bestValue = value;
      best = result;
    }
  }
  if (process.env.AI_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`[lookahead] ${playerId} evaluated ${candidateResults.length} candidates, best value=${bestValue.toFixed(2)}`);
  }
  return best;
}

// weights: optional buy-heuristic tuning (see villageHeuristics.js's
// DEFAULT_BUY_WEIGHTS) - passed through to every pickBestBuy call site so
// the self-play tournament harness (scripts/aiTournament.mjs) can
// experiment with different values without touching this file. Every
// existing caller (store/index.js, the UI, every other test) omits it and
// gets today's corrected default behavior unchanged. weights.lookaheadDepth
// > 0 switches to playAITurnWithLookahead's real search instead of the
// single-step decideTopLevelAction choice - 0 (default) preserves today's
// exact behavior.
export function playAITurn(state, playerId, weights = DEFAULT_BUY_WEIGHTS) {
  if (state.meta.phase !== PHASES.AWAITING_ACTION) {
    throw new Error('playAITurn expects to be called at the start of a turn (phase: awaitingAction).');
  }
  if (weights.lookaheadDepth > 0) {
    return playAITurnWithLookahead(state, playerId, weights);
  }
  switch (decideTopLevelAction(state, playerId, weights)) {
    case 'dungeon': return playDungeonTurn(state, playerId);
    case 'rest': return playRestTurn(state, playerId, weights);
    case 'prepare': return playPrepareTurn(state, playerId, weights);
    default: return playVillageTurn(state, playerId, weights);
  }
}
